import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ShieldAlert,
  Trash2,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  GitPullRequest,
  ExternalLink,
  Pencil,
  Check,
  X,
  CheckCircle2,
} from 'lucide-react';
import { LoadingState, Spinner } from '@systemdox/ui';
import { guardrailsApi, type GuardrailEntry, type CiSettings } from '@/services/guardrailsApi';
import { PageEmptyState } from '@/components/onboarding/PageEmptyState';
import { EditableGuardrailEntry } from '@/components/guardrails/EditableGuardrailEntry';
import { GuardrailEntryView } from '@/components/guardrails/GuardrailEntryView';
import { usePublishJob } from '@/contexts/PublishJobContext';
import { cn } from '@/lib/utils';

function getSafeName(title: string): string {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 60);
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'text-red-600 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
  high: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
  medium: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
  low: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
};

export default function GuardrailsTab() {
  const [entries, setEntries] = useState<GuardrailEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<GuardrailEntry | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishedScripts, setPublishedScripts] = useState<Record<string, string>>({});
  const [publishingSingleId, setPublishingSingleId] = useState<string | null>(null);

  const { publishing: publishingCi, result: ciResult, error: ciError, start: startPublishToCi } = usePublishJob();
  const [ciSettings, setCiSettings] = useState<CiSettings | null>(null);

  useEffect(() => {
    guardrailsApi.listEntries().then(setEntries).finally(() => setLoading(false));
    guardrailsApi.getCiSettings().then(setCiSettings).catch(() => {});
  }, []);

  const loadPublishedScripts = useCallback(() => {
    guardrailsApi.getPublishedScripts().then(setPublishedScripts).catch(() => {});
  }, []);

  useEffect(() => {
    if (ciSettings?.configured) {
      loadPublishedScripts();
    }
  }, [ciSettings, loadPublishedScripts]);

  // Refresh published scripts after a bulk CI publish completes
  useEffect(() => {
    if (ciResult) {
      loadPublishedScripts();
    }
  }, [ciResult, loadPublishedScripts]);

  // Split entries into draft (unpublished) and published groups
  const { draftEntries, publishedEntries } = useMemo(() => {
    if (!ciSettings?.configured) {
      return { draftEntries: entries, publishedEntries: [] as GuardrailEntry[] };
    }
    const draft: GuardrailEntry[] = [];
    const published: GuardrailEntry[] = [];
    for (const entry of entries) {
      const safeName = getSafeName(entry.title);
      if (publishedScripts[safeName]) {
        published.push(entry);
      } else {
        draft.push(entry);
      }
    }
    return { draftEntries: draft, publishedEntries: published };
  }, [entries, publishedScripts, ciSettings]);

  async function handleDelete(id: string) {
    await guardrailsApi.deleteEntry(id);
    setEntries((prev) => prev.filter((e) => e.id !== id));
    if (editingId === id) {
      setEditingId(null);
      setEditDraft(null);
    }
  }

  function startEditing(entry: GuardrailEntry) {
    setEditingId(entry.id);
    setEditDraft({ ...entry });
  }

  function cancelEditing() {
    setEditingId(null);
    setEditDraft(null);
  }

  async function saveEdit() {
    if (!editDraft || !editingId) return;
    setSaving(true);
    try {
      const updated = await guardrailsApi.updateEntry(editingId, {
        title: editDraft.title,
        description: editDraft.description,
        category: editDraft.category,
        severity: editDraft.severity,
        pattern: editDraft.pattern,
        fix: editDraft.fix,
        fitnessTest: editDraft.fitnessTest,
      });
      setEntries((prev) => prev.map((e) => (e.id === editingId ? updated : e)));
      setEditingId(null);
      setEditDraft(null);
    } finally {
      setSaving(false);
    }
  }

  function updateEditField(field: string, value: string) {
    if (!editDraft) return;
    setEditDraft({ ...editDraft, [field]: value });
  }

  async function handlePublishSingle(entry: GuardrailEntry) {
    setPublishingSingleId(entry.id);
    try {
      const jobId = await guardrailsApi.publishSingleToCi(entry.id);
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const status = await guardrailsApi.getJob(jobId);
        if (status.status === 'completed') {
          loadPublishedScripts();
          break;
        }
        if (status.status === 'failed') break;
      }
    } finally {
      setPublishingSingleId(null);
    }
  }

  if (loading) {
    return <LoadingState size="lg" />;
  }

  function renderEntryCard(entry: GuardrailEntry, mode: 'draft' | 'published') {
    const isEditing = editingId === entry.id;
    const isExpanded = expandedId === entry.id;
    const scriptUrl = publishedScripts[getSafeName(entry.title)] || null;

    return (
      <div
        key={entry.id}
        className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden"
      >
        <button
          onClick={() => setExpandedId(isExpanded ? null : entry.id)}
          className="w-full flex items-center justify-between px-4 py-3 text-left"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle
              className={cn(
                'w-4 h-4 shrink-0',
                entry.severity === 'critical'
                  ? 'text-red-500'
                  : entry.severity === 'high'
                    ? 'text-orange-500'
                    : entry.severity === 'medium'
                      ? 'text-amber-500'
                      : 'text-blue-500'
              )}
            />
            <div>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                {entry.title}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <span
                  className={cn(
                    'text-[10px] px-1.5 py-0.5 rounded font-medium border',
                    SEVERITY_COLORS[entry.severity]
                  )}
                >
                  {entry.severity}
                </span>
                <span className="text-xs text-slate-500">{entry.category}</span>
              </div>
            </div>
          </div>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          )}
        </button>

        {isExpanded && (
          <div className="border-t border-slate-200 dark:border-slate-800 px-4 py-4 space-y-4">
            {isEditing && editDraft ? (
              <>
                <EditableGuardrailEntry
                  entry={editDraft}
                  editing
                  onUpdate={updateEditField}
                />
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={cancelEditing}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" /> Cancel
                  </button>
                  <button
                    onClick={saveEdit}
                    disabled={saving}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                  >
                    {saving ? <Spinner size="sm" tone="current" /> : <Check className="w-3.5 h-3.5" />}
                    Save
                  </button>
                </div>
              </>
            ) : (
              <>
                <GuardrailEntryView entry={entry} />

                <div className="flex items-center justify-end gap-2 pt-1 border-t border-slate-100 dark:border-slate-800">
                  {mode === 'draft' && (
                    <>
                      <button
                        onClick={() => startEditing(entry)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                      {ciSettings?.configured && (
                        <button
                          onClick={() => handlePublishSingle(entry)}
                          disabled={publishingSingleId === entry.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 disabled:opacity-50 transition-colors"
                        >
                          {publishingSingleId === entry.id ? <Spinner size="sm" tone="current" /> : <GitPullRequest className="w-3.5 h-3.5" />}
                          {publishingSingleId === entry.id ? 'Publishing...' : 'Publish to GitHub'}
                        </button>
                      )}
                    </>
                  )}

                  {mode === 'published' && scriptUrl && (
                    <a
                      href={scriptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      View in GitHub
                    </a>
                  )}

                  <button
                    onClick={() => handleDelete(entry.id)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Checks</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Capture common AI mistakes and generate architecture fitness tests to prevent them.
          </p>
          <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
            {ciSettings?.configured ? (
              <>
                Publishing to <span className="font-mono">{ciSettings.owner}/{ciSettings.repo}</span> — the check scripts, their runner, and the workflow that runs them on every PR.
              </>
            ) : (
              <>Choose a target repository in <span className="font-mono">Settings &rarr; CI Integration</span> to publish checks into it.</>
            )}
          </p>
        </div>
      </div>

      {ciResult && (
        <div className="mb-4 px-4 py-3 rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
          <div className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
            <GitPullRequest className="w-4 h-4" />
            {ciResult.prUrl
              ? `PR created: ${ciResult.scriptsCreated} script(s) published`
              : 'Nothing to publish — the target repository is already in sync'}
            {ciResult.scriptsRemoved > 0 && `, ${ciResult.scriptsRemoved} removed`}
          </div>
          {ciResult.prUrl && (
            <a
              href={ciResult.prUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-1.5 text-sm text-emerald-600 dark:text-emerald-400 hover:underline"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              View Pull Request #{ciResult.prNumber}
            </a>
          )}
          {ciResult.prUrl && ciResult.workflowCommitted === false && (
            <p className="mt-1.5 text-xs text-amber-700 dark:text-amber-400">
              The workflow could not be committed — grant the SystemDox GitHub App the
              <strong> Workflows</strong> permission and publish again.
            </p>
          )}
        </div>
      )}

      {ciError && (
        <div className="mb-4 px-4 py-2 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
          {ciError}
        </div>
      )}

      {/* The knowledge base is empty but the target repo still carries managed
          files — publishing now reconciles them away, workflow included. */}
      {entries.length === 0 && ciSettings?.configured && Object.keys(publishedScripts).length > 0 && (
        <div className="mb-4 flex items-center justify-between gap-4 px-4 py-3 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {Object.keys(publishedScripts).length} check(s) are still published to{' '}
            <strong>{ciSettings.owner}/{ciSettings.repo}</strong>.
          </p>
          <button
            onClick={startPublishToCi}
            disabled={publishingCi}
            className="flex items-center gap-2 px-4 py-2 rounded-md border border-slate-300 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 shrink-0"
          >
            {publishingCi ? <Spinner size="sm" tone="current" /> : <GitPullRequest className="w-4 h-4" />}
            {publishingCi ? 'Creating PR...' : 'Remove from GitHub'}
          </button>
        </div>
      )}

      {entries.length === 0 && (
        <PageEmptyState
          icon={ShieldAlert}
          title="No checks yet"
          description="Report common AI mistakes to build your knowledge base — checks work best against an indexed repository."
          action="indexRepo"
        />
      )}

      {ciSettings?.configured ? (
        <>
          {/* Draft checks — not yet in GitHub */}
          {draftEntries.length > 0 && (
            <div className="space-y-3 mb-8">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
                  Unpublished ({draftEntries.length})
                </h3>
                <button
                  onClick={startPublishToCi}
                  disabled={publishingCi}
                  className="flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                >
                  {publishingCi ? <Spinner size="sm" tone="current" /> : <GitPullRequest className="w-4 h-4" />}
                  {publishingCi ? 'Creating PR...' : 'Publish All to GitHub'}
                </button>
              </div>
              <p className="text-xs text-slate-400 dark:text-slate-500">
                These checks have no fitness test script in GitHub yet. Review them, then publish.
              </p>
              {draftEntries.map((entry) => renderEntryCard(entry, 'draft'))}
            </div>
          )}

          {/* Published checks — already in GitHub */}
          {publishedEntries.length > 0 && (
            <div className="space-y-3">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-500 uppercase tracking-wide">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                Published ({publishedEntries.length})
              </h3>
              {publishedEntries.map((entry) => renderEntryCard(entry, 'published'))}
            </div>
          )}
        </>
      ) : (
        entries.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
              Knowledge Base ({entries.length} {entries.length === 1 ? 'entry' : 'entries'})
            </h3>
            {entries.map((entry) => renderEntryCard(entry, 'draft'))}
          </div>
        )
      )}
    </div>
  );
}
